// ./src/components/features/CreateEventWizard/accountDefaults.ts
//
// SEEDING A NEW WIZARD DRAFT FROM THE ACCOUNT'S SAVED DEFAULTS (ROADMAP A2b #53).
//
// ⚠⚠ THIS FILE IS THE POINT OF THE WHOLE ITEM. A preferences page whose values nothing reads is
// worse than no page: it tells a host they have configured something and then asks them again on
// every event. The acceptance test is that creating an event after saving defaults asks FEWER
// questions than it did before.
//
// ⚠ PURE AND SEPARATE FROM THE COMPONENT so `npm run check-event-defaults` can RUN these rules
// rather than grep for them — the same reason `sectionNav.ts` lives apart from `SectionTabs`.
import { AccountPreference } from '@/utils/api';

import { EventWizardDraft, EVENT_FORMAT, EVENT_MODE, GUEST_TRACKING, EventFormat, EventMode, GuestTracking, headcountPartyCap } from './wizardTypes';

const FORMATS: string[] = Object.values(EVENT_FORMAT);
const MODES: string[] = Object.values(EVENT_MODE);
const TRACKINGS: string[] = Object.values(GUEST_TRACKING);

/**
 * A stored choice, or null when it is not one this build knows.
 *
 * ⚠ VALIDATED ON THE WAY IN, not trusted. The column is a plain string, so a value written by an
 * older or newer build (or a hand-edited row) would otherwise be pushed into a segmented control
 * that has no such option — which renders as NOTHING selected, on a step whose Next button is gated
 * on having selected something. The host would be stuck with no error to explain it.
 */
const choice = <T extends string>(value: unknown, allowed: string[]): T | null =>
  typeof value === 'string' && allowed.includes(value) ? (value as T) : null;

/** A timezone this browser can actually format in, or null. */
const timezone = (value: unknown): string | null => {
  const zone = typeof value === 'string' ? value.trim() : '';

  if (!zone) {
    return null;
  }

  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });

    return zone;
  } catch {
    return null;
  }
};

/**
 * Apply the account's defaults to a FRESH draft.
 *
 * ⚠⚠ ONLY EVER CALLED ON A DRAFT THE HOST HAS NOT TOUCHED. Re-applying it later would fight the
 * person typing: they would set the event to Online, and the next render would put it back to the
 * account's In person. The caller seeds once and never again.
 *
 * ⚠ A DEFAULT THAT IS NOT SET CHANGES NOTHING. `defaultTimezone: null` must leave the draft's
 * browser-detected zone alone — writing null over it would make event creation WORSE for every host
 * who never set one, which is precisely the failure this item exists to prevent.
 *
 * ⚠ THE VENUE IS ONLY OFFERED WHEN THE EVENT IS ACTUALLY SOMEWHERE. An online event has no venue,
 * and stamping the account's usual hall onto one would be both wrong and invisible — the event
 * looks configured.
 */
export const applyAccountDefaults = (draft: EventWizardDraft, preference: AccountPreference | null | undefined): EventWizardDraft => {
  if (!preference) {
    return draft;
  }

  const next: EventWizardDraft = { ...draft };

  const format = choice<EventFormat>(preference.defaultEventFormat, FORMATS);
  const mode = choice<EventMode>(preference.defaultEventMode, MODES);
  const tracking = choice<GuestTracking>(preference.defaultGuestTracking, TRACKINGS);
  const zone = timezone(preference.defaultTimezone);
  const venueId = typeof preference.defaultVenueId === 'string' ? preference.defaultVenueId.trim() : '';

  if (format) {
    next.eventFormat = format;
  }

  if (mode) {
    next.eventMode = mode;
  }

  if (tracking) {
    next.guestTracking = tracking;
  }

  // #139 — a headcount reply counts a party: started at 1 per reply, the public form would never ask how many are coming.
  // ⓘ Not on a ticketed event: there the tickets are the count, and its own "tickets per order" limit applies (#77).
  if (next.guestTracking === GUEST_TRACKING.HEADCOUNT && next.eventMode !== EVENT_MODE.TICKETED) {
    next.rsvpGroupSize = headcountPartyCap(next.rsvpGroupSize);
  }

  if (zone) {
    next.timezone = zone;
  }

  const isInPerson = next.eventFormat === EVENT_FORMAT.IN_PERSON || next.eventFormat === EVENT_FORMAT.HYBRID;

  if (venueId && isInPerson) {
    next.venueId = venueId;
  }

  return next;
};
